<template>
  <div id="app">
    <!-- Loader to handle assets loading for game -->
    <transition appear @enter="enter" @leave="leave">
      <Loader v-if="!$store.state.loaded"/>
    </transition>

    <GameFrame ref="view" />

    <CookieBar v-if="!$inApp && $store.state.mode === 'intro'" />
    <!-- Utility component to force portrate mode -->
    <RotateDevice />
  </div>
</template>

<script>
import { gsap } from 'gsap';
import { debounce } from 'lodash';
import { CustomEase } from '@/assets/libs/CustomEase';

import Loader from '@/components/Loader.vue';
import GameFrame from '@/components/GameFrame.vue';
import RotateDevice from '@/components/RotateDevice/index.vue';
import CookieBar from '@/components/CookieBar.vue';

import transitions from '@/scripts/transitions';
import { isTouchDevice } from '@/scripts/utils';

gsap.registerPlugin(CustomEase);
CustomEase.create('flap', '0.35, 1.30, 0.65, 1');

export default {
  name: 'App',

  components: {
    Loader,
    GameFrame,
    RotateDevice,
    CookieBar,
  },

  mounted() {
    this.resize();
    window.addEventListener('resize', debounce(() => this.resize(), 200));
    window.addEventListener('blur', () => {
      if (this.$store.state.mode === 'playing') this.$eventHub.$emit('pause');
    });
  },

  methods: {
    checkTutorialState() {
      if (this.$inApp) {
        this.$store.state.tutorialDone = this.$store.state.userData.score > 0;
      } else {
        const localstorageVal = window.localStorage.getItem('guccci_ladybug_tutorial_done');

        if (localstorageVal) {
          this.$store.state.tutorialDone = parseInt(localstorageVal, 10) > 0;
        } else {
          localStorage.setItem('guccci_ladybug_tutorial_done', 1);
        }
      }
    },
    resize() {
      const vh = window.innerHeight * 0.01;
      const h = window.innerHeight * (((isTouchDevice() && window.innerWidth >= 568) || !isTouchDevice()) ? 0.85 : 1);
      const rw = h / 812;

      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.documentElement.style.setProperty('--rw', `${rw}`);

      this.$eventHub.$emit('window-resize');
    },
    loadGame() {
      const overlayComponent = this.$refs.view.$refs.overlay;
      const promises = [
        this.$game.load(),
        overlayComponent.load(),
      ];

      if (this.$inApp) promises.push(this.$store.dispatch('getUserData'));

      return Promise.all(promises).then(() => {
        overlayComponent.replaceImgs();
        this.$game.setUpGame();
        this.$game.resume();
        this.$store.commit('SET_MODE', 'intro');
        this.$store.commit('SET_LOADED', true);
        this.checkTutorialState();
      });
    },
    enter(el, done) {
      transitions.get('loader-enter')(el).then(() => {
        done();
        this.loadGame();
      });
    },
    leave(el, done) {
      const tl = gsap.timeline({ onComplete: done });

      tl.add('start')
        .add(transitions.get('loader-leave')(el), 'start+=1.5')
        .add(() => {
          this.$store.state.showOverlay = !(window.innerWidth < 568 && window.innerWidth / window.innerHeight === 375 / 812);
        }, 'start+=2')
        .add(transitions.get('intro-enter')(this.$refs.view.$el), 'start+=3');
    },
  },
};
</script>

<style lang="scss">
@import '@/scss/base.scss';
</style>
